import React from 'react';
import { Icon } from '@flixbus/honeycomb-icons-react';
import * as reactUtils from '../react-utils/react-utils.js';
import Label from '../../components/_atoms/label/label.js';

/**
 * Concatenates original component extraClasses prop (when available) with new classes to inject.
 * Use case: when cloning children to preserve user given extraClasses.
 * @param {ReactElement} component
 * @param {string} injectedExtraClasses
 * @returns 'original-component-extraClasses' + 'injected-extra-classes'
 */
function handleExtraClasses(component, injectedExtraClasses) {
  const compExtraClasses = component.props.extraClasses ? "".concat(component.props.extraClasses, " ") : '';
  return "".concat(compExtraClasses).concat(injectedExtraClasses);
}
/**
 * Creates prioritized description ids by pushing all possible descriptions in order:
 * 1st: the left inline label (if present);
 * 2nd: the right inline label (if present);
 * 3rd: the inputInfo id (if `info` is present));
 * 4th: any user added aria-describedby coming from component handler prop or the component itself;
 */
function handleAriaDescribedBy(id, _ref) {
  let {
    inlineLabel,
    inlineLabelLeft,
    inlineLabelRight,
    info,
    srOnlyInfo,
    ariaDescribedBy,
    component
  } = _ref;
  const describedByIds = [];
  if (inlineLabel) describedByIds.push("inline-label-".concat(id));
  if (inlineLabelLeft) describedByIds.push("inline-label-left-".concat(id));
  if (inlineLabelRight) describedByIds.push("inline-label-right-".concat(id));
  if (info) describedByIds.push("info-".concat(id));
  if (srOnlyInfo) describedByIds.push("info-srOnly-".concat(id));
  if (ariaDescribedBy) describedByIds.push(ariaDescribedBy.trim());
  if (component && component.props['aria-describedby']) describedByIds.push(component.props['aria-describedby'].trim());
  return describedByIds.length ? describedByIds.join(' ').trim() : undefined;
}
/**
 * Creates a Label component for an input with a given node as text and respective for={id} and
 * component extraClasses.
 *
 * Or clones a given Label component adding the input specific props.
 *
 * Used by input components to handle their given label props.
 *
 * @param {LabelComp | ReactNode} label Label component or a ReactNode
 * @param {string} inputId The input id used on the label `for` attribute
 * @param {HoneyBemInstance} bem The parent bem instance
 * @param {string=} id id for the label
 * @param {string=} requiredText Deprecated: "required" text for generated Label
 * @returns A Label component for the input.
 */
function handleLabel(_ref2) {
  let {
    label,
    inputId,
    bem,
    id,
    requiredText
  } = _ref2;
  if (reactUtils.isOfType(label, 'Label')) {
    const labelComponent = label;
    return /*#__PURE__*/React.cloneElement(labelComponent, {
      extraClasses: handleExtraClasses(labelComponent, bem.e('label')),
      labelFor: inputId
    });
  }
  return /*#__PURE__*/React.createElement(Label, {
    id: id,
    text: label,
    labelFor: inputId,
    requiredText: requiredText,
    extraClasses: bem.e('label')
  });
}
/**
 * Creates an Icon using the provided InlineIcon according to active state.
 * @param InlineIcon
 * @param {boolean} active the active flag to choose an icon.
 * @returns {JSXElement} The DOM node to use as the Portal target.
 */
function handleInlineIcon(InlineIcon, active, iconRest) {
  var _a;
  if (!InlineIcon) return null;
  const iconComp = InlineIcon;
  if ((_a = iconComp.displayName) === null || _a === void 0 ? void 0 : _a.includes('InlineIcon')) {
    return /*#__PURE__*/React.createElement(Icon, Object.assign({}, iconRest, {
      InlineIcon: iconComp,
      "aria-hidden": "true"
    }));
  }
  const inlineIconAsObj = InlineIcon;
  const computedIcon = active ? inlineIconAsObj.active : inlineIconAsObj.regular;
  return /*#__PURE__*/React.createElement(Icon, Object.assign({}, iconRest, {
    InlineIcon: computedIcon,
    "aria-hidden": "true"
  }));
}
function isAriaExpanded(ariaExpanded) {
  if (ariaExpanded === 'false') {
    return false;
  }
  return Boolean(ariaExpanded);
}

export { handleAriaDescribedBy, handleExtraClasses, handleInlineIcon, handleLabel, isAriaExpanded };
